import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { formatToUKDate } from '../../utils/dateFormat';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { 
  X, User, Mail, Phone, FileText, MapPin, Calendar, 
  Image as ImageIcon, Save, UserPlus, XCircle, MessageSquare, Trash2, CheckCircle2, AlertTriangle, Clock,
  Copy, Check
} from 'lucide-react';
import { toast } from 'sonner';
import RescheduleAndMessagePanel from './RescheduleAndMessagePanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  'New': 'bg-blue-100 text-blue-800',
  'Assigned': 'bg-purple-100 text-purple-800',
  'More Information Needed': 'bg-yellow-100 text-yellow-800',
  'Completed': 'bg-green-100 text-green-800',
  'Unrepairable': 'bg-orange-100 text-orange-800',
  'Cancelled': 'bg-red-100 text-red-800',
  'No Show': 'bg-rose-100 text-rose-800',
};

const CANCELLATION_REASONS = [
  'Customer Request',
  'Item Not Repairable',
  'Missing Parts',
  'No Volunteer Available',
  'Customer No-Show',
  'Other'
];

const UNREPAIRABLE_REASONS = [
  'Parts Not Available',
  'Cost of Repair Too High',
  'Damaged Beyond Economic Repair',
  'Obsolete Technology',
  'Safety Concerns',
  'Other'
];

const COMPLETION_OUTCOMES = [
  'Fixed',
  'No Fault Found',
  'Damaged Beyond Repair',
  'Other'
];

export default function RequestDetailModal({ request, onClose }) {
  const { getAuthHeader, user } = useAuth();
  const [status, setStatus] = useState(request.status);
  const [assignedVolunteer, setAssignedVolunteer] = useState(request.assigned_to_id || '');
  const [cancellationReason, setCancellationReason] = useState(request.cancellation_reason || '');
  const [unrepairableReason, setUnrepairableReason] = useState(request.unrepairable_reason || '');
  const [completionOutcome, setCompletionOutcome] = useState(request.completion_outcome || '');
  const [completionOutcomeOther, setCompletionOutcomeOther] = useState(request.completion_outcome_other || '');
  const [deviceType, setDeviceType] = useState(request.device_type || '');
  const [partsRequired, setPartsRequired] = useState(request.parts_required || '');
  const [safetyNotes, setSafetyNotes] = useState(request.safety_notes || '');
  
  // n8n integration fields (for display/editing if needed later, but not in assignment UI)
  const [moreInfoReason, setMoreInfoReason] = useState(request.more_info_reason || '');
  const [unableToFixReason, setUnableToFixReason] = useState(request.unable_to_fix_reason || '');
  
  const [volunteers, setVolunteers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [copiedField, setCopiedField] = useState(null);  // 'email' | 'phone' | null

  // Copy any string to clipboard and surface a small "Copied" pill for 1.5s.
  // Falls back to a hidden textarea + execCommand for older browsers / non-https.
  const handleCopy = async (value, fieldKey) => {
    if (!value) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const ta = document.createElement('textarea');
        ta.value = value;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopiedField(fieldKey);
      setTimeout(() => setCopiedField(k => (k === fieldKey ? null : k)), 1500);
    } catch (_) {
      toast.error('Copy failed — please copy manually.');
    }
  };
  
  // Check if current user is admin or super_admin
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  useEffect(() => {
    fetchVolunteers();
    fetchCategories();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchVolunteers = async () => {
    try {
      const response = await axios.get(`${API}/volunteers`, {
        headers: getAuthHeader()
      });
      setVolunteers(response.data);
    } catch (error) {
      console.error('Error fetching volunteers:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories?active_only=false`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSaveAll = async () => {
    if (status === 'Cancelled' && !cancellationReason) {
      toast.error('Please select a cancellation reason');
      return;
    }

    if (status === 'Unrepairable' && !unableToFixReason) {
      toast.error('Please provide a reason why the item is unrepairable');
      return;
    }

    // Removed validation - automated message will be used

    if (status === 'Completed' && !completionOutcome) {
      toast.error('Please select a completion outcome');
      return;
    }

    if (completionOutcome === 'Other' && !completionOutcomeOther.trim()) {
      toast.error('Please provide details for "Other" outcome');
      return;
    }

    setSaving(true);
    try {
      // 1. Update status, assignment, and details
      const updateData = {
        status,
        device_type: deviceType || null,
        parts_required: partsRequired || null,
        safety_notes: safetyNotes || null,
        cancellation_reason: status === 'Cancelled' ? cancellationReason : null,
        unable_to_fix_reason: status === 'Unrepairable' ? unableToFixReason : null,
        more_info_reason: status === 'More Information Needed' ? 'Automated message' : null,
        completion_outcome: status === 'Completed' ? completionOutcome : null,
        completion_outcome_other: status === 'Completed' && completionOutcome === 'Other' ? completionOutcomeOther : null
      };

      if (assignedVolunteer && status === 'Assigned') {
        const volunteer = volunteers.find(v => v.id === assignedVolunteer);
        updateData.assigned_to_name = volunteer?.name;
        updateData.assigned_to_id = volunteer?.id;
      }

      await axios.put(
        `${API}/repair-requests/${request.id}/status`,
        updateData,
        { headers: getAuthHeader() }
      );

      // 2. Add note if there is one (all notes are now internal)
      if (newNote.trim()) {
        await axios.post(
          `${API}/repair-requests/${request.id}/notes`,
          { 
            content: newNote,
            is_internal: true  // All notes are internal
          },
          { headers: getAuthHeader() }
        );
      }

      toast.success('Changes saved successfully');
      setNewNote('');
      onClose();
    } catch (error) {
      console.error('Error saving changes:', error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRequest = async () => {
    setSaving(true);
    try {
      await axios.delete(
        `${API}/repair-requests/${request.id}`,
        { headers: getAuthHeader() }
      );

      toast.success('Request deleted successfully');
      onClose();
    } catch (error) {
      console.error('Error deleting request:', error);
      toast.error('Failed to delete request');
    } finally {
      setSaving(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[#193460] text-white p-4 sm:p-6 flex justify-between items-center gap-3 rounded-t-xl">
          <div className="min-w-0">
            <h2 className="text-lg sm:text-2xl font-bold truncate">Repair Request Details</h2>
            <p className="text-xs sm:text-sm text-gray-200 mt-1 truncate">ID: {request.id.substring(0, 8)}...</p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors flex-shrink-0"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Customer Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-[#193460] mb-4 flex items-center">
              <User className="w-5 h-5 mr-2" />
              Customer Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="min-w-0">
                <Label className="text-sm text-gray-600">Name</Label>
                <p className="font-medium text-[#193460] break-words">{request.customer_name}</p>
              </div>
              <div className="min-w-0">
                <Label className="text-sm text-gray-600">Email</Label>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-[#193460] flex items-center min-w-0">
                    <Mail className="w-4 h-4 mr-2 text-gray-500 flex-shrink-0" />
                    <span className="truncate" data-testid="customer-email">{request.customer_email}</span>
                  </p>
                  <button
                    type="button"
                    onClick={() => handleCopy(request.customer_email, 'email')}
                    className="inline-flex items-center gap-1 text-xs text-[#193460] hover:text-[#E87B6A] bg-white border border-gray-200 hover:border-[#E87B6A] rounded px-2 py-1 transition-colors"
                    title="Copy email to clipboard"
                    data-testid="copy-email-btn"
                  >
                    {copiedField === 'email' ? (
                      <>
                        <Check className="w-3 h-3 text-green-600" />
                        <span className="text-green-700">Copied</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3" />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                  <a
                    href={`mailto:${request.customer_email}`}
                    className="inline-flex items-center gap-1 text-xs text-white bg-[#E87B6A] hover:bg-[#d66a59] rounded px-2 py-1 transition-colors"
                    title="Open your email client"
                    data-testid="mailto-email-btn"
                  >
                    <Mail className="w-3 h-3" />
                    <span>Email</span>
                  </a>
                </div>
              </div>
              <div>
                <Label className="text-sm text-gray-600">Phone</Label>
                <p className="text-[#193460] flex items-center">
                  <Phone className="w-4 h-4 mr-2 text-gray-500" />
                  {request.customer_phone}
                </p>
              </div>
              <div>
                <Label className="text-sm text-gray-600">Submitted On</Label>
                <p className="text-[#193460] flex items-center">
                  <Calendar className="w-4 h-4 mr-2 text-gray-500" />
                  {formatToUKDate(request.created_at)}
                </p>
              </div>
            </div>
          </div>

          {/* Item Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-[#193460] mb-4 flex items-center">
              <FileText className="w-5 h-5 mr-2" />
              Item Details
            </h3>
            <div className="space-y-3">
              <div>
                <Label className="text-sm text-gray-600">Item to Repair</Label>
                <p className="font-medium text-[#193460] text-lg">{request.item_name}</p>
              </div>
              <div>
                <Label className="text-sm text-gray-600">Description of Fault</Label>
                <p className="text-gray-700 leading-relaxed">{request.item_description}</p>
              </div>
              {request.category_ids && request.category_ids.length > 0 && (
                <div>
                  <Label className="text-sm text-gray-600 mb-2 block">Categories</Label>
                  <div className="flex flex-wrap gap-2">
                    {request.category_ids.map(catId => {
                      const category = categories.find(c => c.id === catId);
                      return category ? (
                        <Badge key={catId} className="bg-[#E87B6A] text-white px-3 py-1">
                          {category.name}
                        </Badge>
                      ) : null;
                    })}
                  </div>
                  {request.category_other && (
                    <p className="text-sm text-gray-600 mt-2 italic">Other: {request.category_other}</p>
                  )}
                </div>
              )}
              {request.preferred_event && (
                <div>
                  <Label className="text-sm text-gray-600">Preferred Event/Location</Label>
                  <p className="text-[#193460] flex items-center">
                    <MapPin className="w-4 h-4 mr-2 text-gray-500" />
                    {request.preferred_event}
                  </p>
                </div>
              )}
              {(deviceType || isAdmin) && (
                <div>
                  <Label htmlFor="deviceType" className="text-sm text-gray-600">Device Type</Label>
                  {isAdmin ? (
                    <Input
                      id="deviceType"
                      value={deviceType}
                      onChange={(e) => setDeviceType(e.target.value)}
                      placeholder="e.g., Laptop, Toaster, Bicycle"
                      className="mt-1"
                    />
                  ) : (
                    <p className="text-[#193460]">{deviceType || 'Not specified'}</p>
                  )}
                </div>
              )}
              {(partsRequired || isAdmin) && (
                <div>
                  <Label htmlFor="partsRequired" className="text-sm text-gray-600">Parts Required</Label>
                  {isAdmin ? (
                    <Textarea
                      id="partsRequired"
                      value={partsRequired}
                      onChange={(e) => setPartsRequired(e.target.value)}
                      placeholder="List any parts needed for this repair"
                      rows={2}
                      className="mt-1"
                    />
                  ) : (
                    <p className="text-[#193460]">{partsRequired || 'None specified'}</p>
                  )}
                </div>
              )}
              {(safetyNotes || isAdmin) && (
                <div>
                  <Label htmlFor="safetyNotes" className="text-sm text-gray-600 flex items-center text-orange-600">
                    <AlertTriangle className="w-4 h-4 mr-1" />
                    Safety Notes
                  </Label>
                  {isAdmin ? (
                    <Textarea
                      id="safetyNotes"
                      value={safetyNotes}
                      onChange={(e) => setSafetyNotes(e.target.value)}
                      placeholder="Any safety concerns or warnings"
                      rows={2}
                      className="mt-1 border-orange-300"
                    />
                  ) : (
                    <p className="text-orange-700 bg-orange-50 p-2 rounded">{safetyNotes || 'None'}</p>
                  )}
                </div>
              )}
              {request.image_urls && request.image_urls.length > 0 && (
                <div>
                  <Label className="text-sm text-gray-600 mb-2 block flex items-center">
                    <ImageIcon className="w-4 h-4 mr-2" />
                    Item Photo
                  </Label>
                  <img 
                    src={request.image_urls[0]} 
                    alt="Item"
                    className="rounded-lg border-2 border-gray-300 max-w-md"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Status Management - Only for Admins or limited for Volunteers */}
          {isAdmin ? (
            <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-200">
              <h3 className="text-lg font-semibold text-[#193460] mb-4">Update Request</h3>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="status" className="text-base font-semibold text-[#193460] mb-2 block">
                    Status
                  </Label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="h-12 border-2 border-gray-300 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="New">New</SelectItem>
                      <SelectItem value="Assigned">Assigned</SelectItem>
                      <SelectItem value="More Information Needed">More Information Needed</SelectItem>
                      <SelectItem value="Completed">Completed</SelectItem>
                      <SelectItem value="Unrepairable">Unrepairable</SelectItem>
                      <SelectItem value="Cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {status === 'Assigned' && (
                  <div>
                    <Label htmlFor="volunteer" className="text-base font-semibold text-[#193460] mb-2 flex items-center">
                      <UserPlus className="w-4 h-4 mr-2" />
                      Assign to Volunteer
                    </Label>
                    <Select value={assignedVolunteer} onValueChange={setAssignedVolunteer}>
                      <SelectTrigger className="h-12 border-2 border-gray-300 bg-white">
                        <SelectValue placeholder="Select a volunteer" />
                      </SelectTrigger>
                      <SelectContent>
                        {volunteers.map(volunteer => (
                          <SelectItem key={volunteer.id} value={volunteer.id}>
                            {volunteer.name} ({volunteer.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

              {status === 'Cancelled' && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="reason" className="text-base font-semibold text-[#193460] mb-2 flex items-center">
                      <XCircle className="w-4 h-4 mr-2" />
                      Cancellation Reason
                    </Label>
                    <Select value={cancellationReason?.startsWith('Other') ? 'Other' : cancellationReason} onValueChange={setCancellationReason}>
                      <SelectTrigger className="h-12 border-2 border-gray-300 bg-white">
                        <SelectValue placeholder="Select a reason" />
                      </SelectTrigger>
                      <SelectContent>
                        {CANCELLATION_REASONS.map(reason => (
                          <SelectItem key={reason} value={reason}>
                            {reason}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {(cancellationReason === 'Other' || cancellationReason?.startsWith('Other:')) && (
                    <div>
                      <Label htmlFor="cancellationOther" className="text-base font-semibold text-[#193460] mb-2">
                        Please specify reason
                      </Label>
                      <Textarea
                        id="cancellationOther"
                        value={cancellationReason?.startsWith('Other:') ? cancellationReason.replace('Other: ', '') : ''}
                        onChange={(e) => {
                          // Update the cancellation reason with the custom text
                          setCancellationReason(`Other: ${e.target.value}`);
                        }}
                        placeholder="Enter the specific reason for cancellation..."
                        rows={3}
                        className="border-2 border-gray-300 focus:border-[#E87B6A]"
                      />
                    </div>
                  )}
                </div>
              )}

              {status === 'Unrepairable' && (
                <div>
                  <Label htmlFor="unableToFixReason" className="text-base font-semibold text-[#193460] mb-2 flex items-center">
                    <XCircle className="w-4 h-4 mr-2" />
                    Why Unable to Fix
                  </Label>
                  <Textarea
                    id="unableToFixReason"
                    value={unableToFixReason}
                    onChange={(e) => setUnableToFixReason(e.target.value)}
                    placeholder="Explain why this item cannot be repaired..."
                    rows={3}
                    className="border-2 border-gray-300 focus:border-[#E87B6A]"
                  />
                </div>
              )}

              {/* More Information status selected - automated message will be sent */}
              {status === 'More Information Needed' && (
                <div className="bg-amber-50 p-4 rounded-lg border-2 border-amber-200">
                  <p className="text-sm text-amber-800 flex items-center">
                    <AlertTriangle className="w-4 h-4 mr-2 text-amber-600" />
                    An automated message will be sent to the customer requesting more information.
                  </p>
                </div>
              )}

              {status === 'Completed' && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="outcome" className="text-base font-semibold text-[#193460] mb-2 flex items-center">
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Completion Outcome
                    </Label>
                    <Select value={completionOutcome} onValueChange={setCompletionOutcome}>
                      <SelectTrigger className="h-12 border-2 border-gray-300 bg-white">
                        <SelectValue placeholder="Select outcome" />
                      </SelectTrigger>
                      <SelectContent>
                        {COMPLETION_OUTCOMES.map(outcome => (
                          <SelectItem key={outcome} value={outcome}>
                            {outcome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {completionOutcome === 'Other' && (
                    <div>
                      <Label htmlFor="outcomeOther" className="text-base font-semibold text-[#193460] mb-2 block">
                        Please Specify
                      </Label>
                      <Textarea
                        value={completionOutcomeOther}
                        onChange={(e) => setCompletionOutcomeOther(e.target.value)}
                        placeholder="Describe the outcome..."
                        rows={2}
                        className="border-2 border-gray-300 focus:border-[#E87B6A]"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          ) : (
            /* Volunteer View - Can update status to Completed, Unrepairable, or Cancelled */
            <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-200">
              <h3 className="text-lg font-semibold text-[#193460] mb-4">Update Status</h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-base font-semibold text-[#193460] mb-2 block">
                    Status
                  </Label>
                  <Select 
                    value={status} 
                    onValueChange={setStatus}
                    disabled={status === 'Completed'}
                  >
                    <SelectTrigger className="h-12 border-2 border-gray-300 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={request.status}>{request.status}</SelectItem>
                      {request.status !== 'More Information Needed' && (
                        <SelectItem value="More Information Needed">More Information Needed</SelectItem>
                      )}
                      {request.status !== 'Completed' && (
                        <SelectItem value="Completed">Completed</SelectItem>
                      )}
                      {request.status !== 'Unrepairable' && (
                        <SelectItem value="Unrepairable">Unrepairable</SelectItem>
                      )}
                      {request.status !== 'Cancelled' && (
                        <SelectItem value="Cancelled">Cancelled</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-gray-600 mt-2">
                    Mark as completed, unrepairable, cancelled, or request more information as needed.
                  </p>
                </div>

                {status === 'Completed' && (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="outcome" className="text-base font-semibold text-[#193460] mb-2 flex items-center">
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Completion Outcome
                      </Label>
                      <Select value={completionOutcome} onValueChange={setCompletionOutcome}>
                        <SelectTrigger className="h-12 border-2 border-gray-300 bg-white">
                          <SelectValue placeholder="Select outcome" />
                        </SelectTrigger>
                        <SelectContent>
                          {COMPLETION_OUTCOMES.map(outcome => (
                            <SelectItem key={outcome} value={outcome}>
                              {outcome}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {completionOutcomeOther === 'Other' && (
                      <div>
                        <Label htmlFor="outcomeOther" className="text-base font-semibold text-[#193460] mb-2 block">
                          Please Specify
                        </Label>
                        <Textarea
                          value={completionOutcomeOther}
                          onChange={(e) => setCompletionOutcomeOther(e.target.value)}
                          placeholder="Describe the outcome..."
                          rows={2}
                          className="border-2 border-gray-300 focus:border-[#E87B6A]"
                        />
                      </div>
                    )}
                  </div>
                )}
                
                {/* Unrepairable Reason - For Volunteers */}
                {status === 'Unrepairable' && (
                  <div>
                    <Label htmlFor="unableToFixReason" className="text-base font-semibold text-[#193460] mb-2 flex items-center">
                      <XCircle className="w-4 h-4 mr-2 text-orange-600" />
                      Why Unable to Fix? <span className="text-red-600 ml-1">*</span>
                    </Label>
                    <Textarea
                      id="unableToFixReason"
                      value={unableToFixReason}
                      onChange={(e) => setUnableToFixReason(e.target.value)}
                      placeholder="Explain why this item cannot be repaired (e.g., parts not available, beyond economic repair, safety concerns)..."
                      rows={3}
                      className="border-2 border-orange-300 focus:border-orange-500"
                    />
                    <p className="text-sm text-orange-700 mt-2">
                      This explanation will be sent to the customer.
                    </p>
                  </div>
                )}

                {/* Cancellation Reason - For Volunteers */}
                {status === 'Cancelled' && (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="cancelReason" className="text-base font-semibold text-[#193460] mb-2 flex items-center">
                        <XCircle className="w-4 h-4 mr-2" />
                        Cancellation Reason <span className="text-red-600 ml-1">*</span>
                      </Label>
                      <Select value={cancellationReason?.startsWith('Other') ? 'Other' : cancellationReason} onValueChange={setCancellationReason}>
                        <SelectTrigger className="h-12 border-2 border-gray-300 bg-white">
                          <SelectValue placeholder="Select a reason" />
                        </SelectTrigger>
                        <SelectContent>
                          {CANCELLATION_REASONS.map(reason => (
                            <SelectItem key={reason} value={reason}>
                              {reason}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {(cancellationReason === 'Other' || cancellationReason?.startsWith('Other:')) && (
                      <div>
                        <Label htmlFor="cancellationOther" className="text-base font-semibold text-[#193460] mb-2">
                          Please specify reason
                        </Label>
                        <Textarea
                          id="cancellationOther"
                          value={cancellationReason?.startsWith('Other:') ? cancellationReason.replace('Other: ', '') : ''}
                          onChange={(e) => {
                            setCancellationReason(`Other: ${e.target.value}`);
                          }}
                          placeholder="Enter the specific reason for cancellation..."
                          rows={3}
                          className="border-2 border-gray-300 focus:border-[#E87B6A]"
                        />
                      </div>
                    )}
                    <p className="text-sm text-gray-600 mt-2">
                      This reason will be sent to the customer.
                    </p>
                  </div>
                )}
                
                {/* Request More Information Button for Volunteers */}
                <div className="border-t-2 border-blue-200 pt-4 mt-4">
                  <Button
                    onClick={() => {
                      setStatus('More Information Needed');
                      toast.info('Please provide a reason for requesting more information below');
                    }}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white h-12"
                    disabled={request.status === 'More Information Needed' || request.status === 'Completed'}
                  >
                    <MessageSquare className="w-5 h-5 mr-2" />
                    Request More Information from Customer
                  </Button>
                  {request.status === 'More Information Needed' && (
                    <p className="text-sm text-amber-700 mt-2">
                      More information has already been requested for this repair.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* More Information - Volunteer section - automated message */}
          {status === 'More Information Needed' && !isAdmin && (
            <div className="bg-amber-50 p-4 rounded-lg border-2 border-amber-200">
              <p className="text-sm text-amber-800 flex items-center">
                <AlertTriangle className="w-4 h-4 mr-2 text-amber-600" />
                An automated message will be sent to the customer requesting more information.
              </p>
            </div>
          )}

          {/* Notes Section */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-[#193460] mb-4 flex items-center">
              <MessageSquare className="w-5 h-5 mr-2" />
              Notes
            </h3>
            
            {/* Existing Notes */}
            {request.notes && request.notes.length > 0 && (
              <div className="space-y-3 mb-4">
                {request.notes.map(note => (
                  <div 
                    key={note.id} 
                    className={`p-3 rounded border ${
                      note.is_internal 
                        ? 'bg-amber-50 border-amber-300' 
                        : 'bg-white border-gray-200'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm text-[#193460]">{note.admin_name}</span>
                        {note.is_internal && (
                          <Badge className="bg-amber-600 text-white text-xs px-2 py-0">
                            Internal
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(note.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-gray-700 text-sm">{note.content}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Add New Note */}
            <div className="space-y-3">
              <Label className="text-sm text-gray-600">Add New Note (Internal - Not visible to customers)</Label>
              <Textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add an internal note about this request..."
                rows={3}
                className="border-2 border-gray-300 focus:border-[#E87B6A]"
              />
              <p className="text-xs text-amber-700">
                All notes are internal and visible only to Admin, Super Admin, and Volunteers.
              </p>
            </div>
          </div>

          {/* Manual override + direct customer messaging panel */}
          <RescheduleAndMessagePanel
            request={request}
            volunteers={volunteers}
            currentUser={user}
            getAuthHeader={getAuthHeader}
            onChanged={() => onClose()}
          />

          {/* Activity Log Section */}
          {request.activity_log && request.activity_log.length > 0 && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-[#193460] mb-4 flex items-center">
                <Clock className="w-5 h-5 mr-2" />
                Activity Log
              </h3>
              <div className="space-y-2">
                {request.activity_log.slice().reverse().map(log => (
                  <div key={log.id} className="flex items-start gap-3 text-sm border-l-2 border-blue-400 pl-3 py-1">
                    <div className="flex-1">
                      <p className="text-gray-700">{log.details}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        by {log.user_name} • {formatToUKDate(log.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Automation Status Tracking */}
          {isAdmin && (
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="text-lg font-semibold text-[#193460] mb-3 flex items-center">
                <CheckCircle2 className="w-5 h-5 mr-2" />
                Automation Status
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  {request.assigned_notification_sent ? (
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                  ) : (
                    <XCircle className="w-4 h-4 text-gray-400" />
                  )}
                  <span className={request.assigned_notification_sent ? 'text-green-700 font-medium' : 'text-gray-500'}>
                    Assignment Sent
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {request.completion_notification_sent ? (
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                  ) : (
                    <XCircle className="w-4 h-4 text-gray-400" />
                  )}
                  <span className={request.completion_notification_sent ? 'text-green-700 font-medium' : 'text-gray-500'}>
                    Completion Sent
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {request.more_information_notification_sent ? (
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                  ) : (
                    <XCircle className="w-4 h-4 text-gray-400" />
                  )}
                  <span className={request.more_information_notification_sent ? 'text-green-700 font-medium' : 'text-gray-500'}>
                    More Info Sent
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {request.unable_to_fix_notification_sent ? (
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                  ) : (
                    <XCircle className="w-4 h-4 text-gray-400" />
                  )}
                  <span className={request.unable_to_fix_notification_sent ? 'text-green-700 font-medium' : 'text-gray-500'}>
                    Unable to Fix Sent
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {request.survey_sent ? (
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                  ) : (
                    <XCircle className="w-4 h-4 text-gray-400" />
                  )}
                  <span className={request.survey_sent ? 'text-green-700 font-medium' : 'text-gray-500'}>
                    Survey Sent
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons at Bottom */}
          <div className="flex gap-3 pt-4 border-t-2 border-gray-200">
            <Button
              onClick={handleSaveAll}
              disabled={saving}
              className="flex-1 bg-[#E87B6A] hover:bg-[#d66a59] text-white h-12 text-base font-semibold"
            >
              <Save className="w-5 h-5 mr-2" />
              {saving ? 'Saving...' : 'Save All Changes'}
            </Button>
            
            {/* Delete button - Admin only */}
            {isAdmin && (
              <>
                {!showDeleteConfirm ? (
                  <Button
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={saving}
                    variant="destructive"
                    className="bg-red-600 hover:bg-red-700 text-white h-12 px-6"
                  >
                    <Trash2 className="w-5 h-5 mr-2" />
                    Delete
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      onClick={handleDeleteRequest}
                      disabled={saving}
                      variant="destructive"
                      className="bg-red-600 hover:bg-red-700 text-white h-12"
                    >
                      Confirm Delete
                    </Button>
                    <Button
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={saving}
                      variant="outline"
                      className="h-12"
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
